// src/shell/Sidebar.tsx
import { NavLink, useLocation } from 'react-router-dom'
import { AppSwitcher } from './AppSwitcher'
import { useLogout } from '../auth/useAuth'

interface NavItem {
  label: string
  to: string
  icon: string
  disabled?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Cockpit', to: '/', icon: 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z' },
  { label: 'CRM', to: '/crm', icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75' },
  { label: 'Stats', to: '/stats', icon: 'M18 20V10M12 20V4M6 20v-6' },
  { label: 'Rapports', to: '/reports', icon: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M8 13h8M8 17h5' },
  { label: 'Marketing', to: '/marketing', icon: 'M22 12h-4l-3 9L9 3l-3 9H2' },
  { label: 'Finance', to: '/finance', icon: 'M2 5h20a1 1 0 011 1v12a1 1 0 01-1 1H2a1 1 0 01-1-1V6a1 1 0 011-1zM1 10h22M6 15h4' },
  { label: 'Site / CMS', to: '/cms', icon: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5', disabled: true },
]

const CRM_SUBNAV: { label: string; to: string }[] = [
  { label: 'Tableau de bord', to: '/crm' },
  { label: 'Pipeline', to: '/crm/pipeline' },
  { label: 'Contacts', to: '/crm/contacts' },
  { label: 'Programme UGC', to: '/crm/ugc' },
  { label: 'Campagnes', to: '/crm/campaigns' },
  { label: 'Templates', to: '/crm/templates' },
  { label: 'Candidatures', to: '/crm/applications' },
  { label: 'Non assignés', to: '/crm/unmatched' },
  { label: 'Tâches', to: '/crm/tasks' },
]

const BOTTOM_ITEMS: NavItem[] = [
  { label: 'Réglages', to: '/settings', icon: 'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z' },
]

function NavIcon({ d }: { d: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d={d} stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const activeLinkStyle = {
  background: 'var(--elevated)',
  color: 'var(--fg)',
  borderColor: 'var(--line)',
}

const baseLinkStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.625rem',
  padding: '0.4375rem 0.625rem',
  borderRadius: '7px',
  border: '1px solid transparent',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--muted)',
  textDecoration: 'none',
  transition: 'background 0.12s, color 0.12s',
  cursor: 'pointer',
  fontFamily: 'inherit',
  width: '100%',
  background: 'transparent',
  textAlign: 'left',
}

const subNavLinkStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '0.3125rem 0.625rem 0.3125rem 2.125rem',
  borderRadius: '6px',
  border: '1px solid transparent',
  fontSize: '0.8125rem',
  fontWeight: 400,
  color: 'var(--muted)',
  textDecoration: 'none',
  transition: 'background 0.12s, color 0.12s',
  width: '100%',
}

function SoonBadge() {
  return (
    <span style={{
      marginLeft: 'auto',
      fontSize: '0.6875rem',
      fontWeight: 600,
      color: 'var(--dim)',
      background: 'var(--elevated)',
      border: '1px solid var(--line)',
      borderRadius: '4px',
      padding: '0.125rem 0.375rem',
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
    }}>
      bientôt
    </span>
  )
}

function CrmSubNav() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.0625rem', marginTop: '0.125rem' }}>
      {CRM_SUBNAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/crm'}
          style={({ isActive }) => ({
            ...subNavLinkStyle,
            ...(isActive
              ? { background: 'var(--elevated)', color: 'var(--fg)', borderColor: 'var(--line)' }
              : {}),
          })}
        >
          {item.label}
        </NavLink>
      ))}
    </div>
  )
}

const MARKETING_SUBNAV: { label: string; to: string }[] = [
  { label: 'Tableau de bord', to: '/marketing' },
  { label: 'Lancement',       to: '/marketing/launch' },
  { label: 'Dossier',         to: '/marketing/docs' },
  { label: 'ASO',             to: '/marketing/aso' },
  { label: 'Contenus',        to: '/marketing/content' },
  { label: 'Prompts',         to: '/marketing/prompts' },
  { label: 'Canaux & budget', to: '/marketing/channels' },
  { label: 'Mesure',          to: '/marketing/mesure' },
]

function MarketingSubNav() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.0625rem', marginTop: '0.125rem' }}>
      {MARKETING_SUBNAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/marketing'}
          style={({ isActive }) => ({
            ...subNavLinkStyle,
            ...(isActive
              ? { background: 'var(--elevated)', color: 'var(--fg)', borderColor: 'var(--line)' }
              : {}),
          })}
        >
          {item.label}
        </NavLink>
      ))}
    </div>
  )
}

const FINANCE_SUBNAV: { label: string; to: string }[] = [
  { label: 'Calculateur', to: '/finance' },
  { label: 'Suivi mensuel', to: '/finance/suivi' },
]

function FinanceSubNav() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.0625rem', marginTop: '0.125rem' }}>
      {FINANCE_SUBNAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/finance'}
          style={({ isActive }) => ({
            ...subNavLinkStyle,
            ...(isActive
              ? { background: 'var(--elevated)', color: 'var(--fg)', borderColor: 'var(--line)' }
              : {}),
          })}
        >
          {item.label}
        </NavLink>
      ))}
    </div>
  )
}

export function Sidebar({
  onOpenCommandPalette,
  open = false,
  onNavigate,
}: {
  onOpenCommandPalette?: () => void
  /** Tiroir ouvert sous 760 px (shell.css). */
  open?: boolean
  /** Appelé à chaque clic sur un lien : la coque referme le tiroir. */
  onNavigate?: () => void
}) {
  const logout = useLogout()
  const { pathname } = useLocation()
  const inCrm = pathname === '/crm' || pathname.startsWith('/crm/')
  const inMarketing = pathname === '/marketing' || pathname.startsWith('/marketing/')
  const inFinance = pathname === '/finance' || pathname.startsWith('/finance/')

  return (
    <aside
      id="shell-sidebar"
      data-testid="sidebar"
      className={open ? 'shell-sidebar shell-sidebar--open' : 'shell-sidebar'}
      onClick={(e) => {
        if (e.target instanceof Element && e.target.closest('a')) onNavigate?.()
      }}
      style={{
        width: '220px',
        minWidth: '220px',
        height: '100%',
        background: 'var(--surface)',
        borderRight: '1px solid var(--line)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
      <AppSwitcher />

      <div style={{ flex: 1, padding: '0 0.5rem', overflowY: 'auto' }}>
        {onOpenCommandPalette && (
          <button
            onClick={onOpenCommandPalette}
            title="Ouvrir la palette (⌘K)"
            aria-label="Ouvrir la palette de commandes"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.375rem 0.625rem',
              background: 'var(--elevated)',
              border: '1px solid var(--line)',
              borderRadius: '6px',
              color: 'var(--dim)',
              fontSize: '0.75rem',
              cursor: 'pointer',
              width: '100%',
              fontFamily: 'inherit',
              marginBottom: '0.5rem',
            }}
          >
            <span style={{ flex: 1, textAlign: 'left' }}>Rechercher…</span>
            <kbd style={{ fontSize: '0.625rem', fontFamily: 'var(--mono)', color: 'var(--dim)', border: '1px solid var(--line)', borderRadius: '3px', padding: '0 0.25rem' }}>⌘K</kbd>
          </button>
        )}
        <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
          {NAV_ITEMS.map((item) =>
            item.disabled ? (
              <div
                key={item.label}
                style={{ ...baseLinkStyle, cursor: 'default', opacity: 0.5 }}
              >
                <NavIcon d={item.icon} />
                <span>{item.label}</span>
                <SoonBadge />
              </div>
            ) : (
              <div key={item.label}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  style={({ isActive }) => ({
                    ...baseLinkStyle,
                    ...(isActive ? activeLinkStyle : {}),
                  })}
                >
                  <NavIcon d={item.icon} />
                  <span>{item.label}</span>
                </NavLink>
                {item.to === '/crm' && inCrm && <CrmSubNav />}
                {item.to === '/marketing' && inMarketing && <MarketingSubNav />}
                {item.to === '/finance' && inFinance && <FinanceSubNav />}
              </div>
            ),
          )}
        </nav>
      </div>

      <div style={{ padding: '0.5rem', borderTop: '1px solid var(--line)' }}>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
          {BOTTOM_ITEMS.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              style={({ isActive }) => ({
                ...baseLinkStyle,
                ...(isActive ? activeLinkStyle : {}),
              })}
            >
              <NavIcon d={item.icon} />
              <span>{item.label}</span>
            </NavLink>
          ))}
          <button
            onClick={() => logout.mutate()}
            style={{ ...baseLinkStyle, color: 'var(--dim)' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Déconnexion</span>
          </button>
        </nav>
      </div>
    </aside>
  )
}
